import { CheatEnum, Defines, EntityGroup, FacingFlag, GK, StateEnum, TeamEnum, type IMoveDemoEntity, type IMoveInfo, type IMoveListData, type IMoveStep, type LGK } from "../../../defines";
import { LFW } from "../../../LFW";
import { StatBarType, type Entity } from "../../../entity";
import type { IUIKeyEvent } from "../../IUIKeyEvent";
import type { UINode } from "../../UINode";
import { Picture } from "../Picture";
import { ScrollView } from "../ScrollView";
import { UIComponent } from "../UIComponent";

const DEFAULT_BG = "bg_move_table";
const DEFAULT_ENTITY_DX = 150;
const DEFAULT_ENTITY_HP = 9999;
const DEFAULT_STEP_WAIT = 100;
const DEFAULT_LOOP_GAP = 150;
const START_DELAY = 1000;
const DEFAULT_END_DELAY = 2000;
const MIN_INPUT_TIME = 200;
const MAX_INPUT_TIME = 4000;
const DEMO_MP = 1000000;
const DEMO_RECT_LEFT = 399;
const DEMO_RECT_RIGHT = 780;
const DEMO_PAD = 45;
const ACTOR_X = (DEMO_RECT_LEFT + DEMO_RECT_RIGHT) / 2;
const ROW_H = 24;
const ROW_CURSOR = "▶ ";
const ROW_INDENT = "   ";
const NO_ENTITIES: IMoveDemoEntity[] = [];
const NO_MOVES: IMoveInfo[] = [];

enum StepKind { Click, Down, Up, Spawn }

interface IKeyStep {
  at: number;
  kind: StepKind;
  keys: string;
  entity?: number;
  spec?: IMoveDemoEntity;
}

export class MoveTableLogic extends UIComponent {
  static override readonly TAGS: string[] = ["MoveTableLogic"];

  private _list_index = 0;
  private _list_ref: IMoveListData | undefined;
  private _move_index = 0;
  private _actor: Entity | null = null;
  private _entities: Entity[] = [];
  private _entity_specs: IMoveDemoEntity[] = [];
  private _entity_fit = 1;
  private _steps: IKeyStep[] = [];
  private _step_index = 0;
  private _move_time = 0;
  private _end_wait = 0;
  private _phase = 0;
  private _loading = false;
  private _char_label: UINode | null = null;
  private _name_label: UINode | null = null;
  private _keys_label: UINode | null = null;
  private _desc_label: UINode | null = null;
  private _face: Picture | null = null;
  private _scroll: ScrollView | null = null;
  private _rows_bound = false;
  private _rows: UINode[] = [];
  private _row_names: UINode[] = [];
  private _row_keys: UINode[] = [];

  private get lists(): IMoveListData[] {
    const moves = this.lfw.datas.moves;
    const cheat_0 = this.lfw.is_cheat(CheatEnum.LF2_NET);
    const cheat_1 = this.lfw.is_cheat(CheatEnum.GIM_INK);
    if (cheat_0 && cheat_1) return moves;
    return moves.filter(v => {
      const groups = this.lfw.datas.find(v.oid)?.base.group;
      if (!cheat_0 && groups?.some(g => g === EntityGroup.Hidden)) return false;
      if (!cheat_1 && groups?.some(g => g === EntityGroup.Dev)) return false;
      return true;
    });
  }
  private get list(): IMoveListData | undefined {
    return this.lists[this._list_index];
  }

  override on_start(): void {
    this._char_label = this.node.search_node("move_table_char") ?? null;
    this._name_label = this.node.search_node("move_table_name") ?? null;
    this._keys_label = this.node.search_node("move_table_keys") ?? null;
    this._desc_label = this.node.search_node("move_table_desc") ?? null;
    this._face = this.node.search_node("move_table_face")?.find_component(Picture) ?? null;
    this._scroll = this.node.search_node("move_list_scroll")?.find_component(ScrollView) ?? null;
    this._rows.length = 0;
    this._row_names.length = 0;
    this._row_keys.length = 0;
    const content = this.node.search_node("move_list_content");
    if (content) {
      for (let i = 0; i < content.children.length; i++) {
        const row = content.children[i];
        row.move_to(0, i * ROW_H, 0);
        if (!this._rows_bound) row.callbacks.add({ on_click: () => this.click_row(i) });
        this._rows.push(row);
        this._row_names.push(row.children[0]);
        this._row_keys.push(row.children[1]);
      }
      this._rows_bound = true;
    }
    if (this.lists.length) this.enter_list(0);
    else this.load_lists();
  }

  private load_lists(): void {
    if (this._loading) return;
    const zips = LFW.ZIPS.slice(1);
    if (!zips.length) return;
    this._loading = true;
    this._char_label?.set_text(this.lfw.string("now_loading"));
    this.lfw.load(...zips).then(
      () => {
        this._loading = false;
        this.enter_list(0);
      },
      (e) => this.warn(`[MoveTableLogic] failed to load data zips: ${e}`),
    );
  }

  override on_stop(): void {
    this.world.clear();
    this._actor = null;
    this._entities.length = 0;
  }

  override on_key_down(e: IUIKeyEvent): void {
    if (!this.lists.length) return;
    else if (GK.L === e.game_key) this.enter_list(this._list_index - 1);
    else if (GK.R === e.game_key) this.enter_list(this._list_index + 1);
    else if (GK.U === e.game_key) this.start_move(this._move_index - 1);
    else if (GK.D === e.game_key) this.start_move(this._move_index + 1);
    else if (GK.a === e.game_key) this.start_move(this._move_index);
    else if (GK.j === e.game_key) this.start_move(this._move_index);
  }

  override update(dt: number): void {
    if (this.world.paused) return;
    const list = this.list;
    if (!list || list !== this._list_ref) {
      if (this.lists.length) this.enter_list(this._list_index);
      return;
    }
    if (!list.moves?.length) return;
    if (!this._actor || this._actor.hp <= 0) {
      this.start_move(this._move_index);
      return;
    }
    this._actor.position.x = this.clamp_x(this._actor.position.x);
    for (const e of this._entities)
      if (e) e.position.x = this.clamp_x(e.position.x);
    this._move_time += dt;
    if (this._phase === 0) {
      while (
        this._step_index < this._steps.length &&
        this._steps[this._step_index].at <= this._move_time
      ) {
        this.exec_step(this._steps[this._step_index++]);
      }
      if (this._step_index >= this._steps.length && this._move_time >= MIN_INPUT_TIME)
        this._phase = 1;
    } else if (this._phase === 1) {
      const idle =
        this._actor.state === StateEnum.Standing ||
        this._actor.state === StateEnum.Walking;
      if (idle || this._move_time >= MAX_INPUT_TIME) {
        this._phase = 2;
        this._end_wait = 0;
      }
    } else {
      this._end_wait += dt;
      const move = list.moves?.[this._move_index];
      if (this._end_wait >= (move?.end_delay ?? list.end_delay ?? DEFAULT_END_DELAY))
        this.start_move(this._move_index);
    }
  }

  private enter_list(index: number): void {
    const lists = this.lists;
    if (!lists.length) return;
    const len = lists.length;
    this._list_index = ((index % len) + len) % len;
    const list = lists[this._list_index];
    const data = this.lfw.datas.find(list.oid);
    this._char_label?.set_text(list.name ?? data?.base.name ?? list.oid);
    this._face?.set_src(data?.base.head ?? Defines.BuiltIn_Imgs.RFACE);
    this.start_move(0);
  }

  private start_move(index: number): void {
    const list = this.list;
    if (!list) return;
    this._list_ref = list;
    const moves = list.moves ?? NO_MOVES;
    const len = moves.length;
    this._move_index = len ? ((index % len) + len) % len : 0;
    const move = moves[this._move_index];
    this.rebuild_scene(list, move);
    if (move?.frame) this.enter_move_frame(move.frame);
    this.build_steps(move);
    this._step_index = 0;
    this._move_time = -(move?.start_delay ?? list.start_delay ?? START_DELAY);
    this._end_wait = 0;
    this._phase = 0;
    this.update_move_labels(list, move);
  }

  private rebuild_scene(list: IMoveListData, move: IMoveInfo | undefined): void {
    this.world.clear();
    this.lfw.change_bg(list.bg ?? DEFAULT_BG);
    this.world.camera.undest();
    this.world.camera.unlock();
    this._entities.length = 0;
    this._entity_specs.length = 0;
    this._actor = this.spawn_actor(list, move);
    this.spawn_entities(move?.entities ?? list.entities ?? NO_ENTITIES);
  }

  private enter_move_frame(id: string): void {
    const actor = this._actor;
    if (!actor) return;
    actor.mp = DEMO_MP;
    actor.enter_frame_by_id(id, true);
  }

  private set_move_keys(node: UINode | null, move: IMoveInfo | undefined): void {
    if (!node) return;
    const key = move?.keys;
    node.set_text(key ? this.lfw.string(key) : "");
  }

  private set_move_name(node: UINode | null, move: IMoveInfo | undefined, prefix = ""): void {
    if (!node) return;
    const key = move?.name;
    node.set_text(key ? prefix + this.lfw.string(key) : "");
  }

  private update_move_labels(list: IMoveListData, move: IMoveInfo | undefined): void {
    this.set_move_name(this._name_label, move);
    this.set_move_keys(this._keys_label, move);
    this._desc_label?.set_text(
      move?.desc
        ? this.lfw.string(move.desc)
        : move?.mp
          ? `${this.lfw.string("move_table.mp")}: ${move.mp}`
          : "",
    );
    const moves = list.moves;
    for (let i = 0; i < this._rows.length; i++) {
      const row = moves?.[i];
      const current = i === this._move_index;
      const row_node = this._rows[i];
      row_node.visible = !!row;
      const name_node = this._row_names[i];
      const keys_node = this._row_keys[i];
      if (name_node) {
        name_node.opacity = current ? 1 : 0.55;
        this.set_move_name(name_node, row, current ? ROW_CURSOR : ROW_INDENT);
      }
      if (keys_node) {
        keys_node.opacity = current ? 1 : 0.55;
        this.set_move_keys(keys_node, row);
      }
    }
    const current_row = this._rows[this._move_index];
    if (current_row) current_row.focused = true;
  }

  private click_row(index: number): void {
    if (this._scroll?.dragging) return;
    if (index >= (this.list?.moves?.length ?? 0)) return;
    this.start_move(index);
  }

  private build_steps(move: IMoveInfo | undefined): void {
    this._steps.length = 0;
    this.push_steps(move?.seq);
    for (let i = 0; i < this._entity_specs.length; i++) {
      const spec = this._entity_specs[i];
      if (spec.time !== undefined)
        this._steps.push({ at: spec.time, kind: StepKind.Spawn, keys: "", entity: i, spec });
      this.push_steps(spec.seq, i);
    }
    this._steps.sort((a, b) => a.at - b.at);
  }

  private push_steps(seq: IMoveStep[] | undefined, entity?: number): void {
    if (!seq?.length) return;
    let at = 0;
    for (const step of seq) {
      at = step.time ?? (at + DEFAULT_STEP_WAIT);
      const loop = Math.max(1, Math.round(step.loop ?? 1));
      const gap = step.gap ?? DEFAULT_LOOP_GAP;
      for (let i = 0; i < loop; i++) {
        const t = at + i * gap;
        if (step.keyups) this._steps.push({ at: t, kind: StepKind.Up, keys: step.keyups, entity });
        if (step.keydowns) this._steps.push({ at: t, kind: StepKind.Down, keys: step.keydowns, entity });
        if (step.clicks) this._steps.push({ at: t, kind: StepKind.Click, keys: step.clicks, entity });
      }
      at += (loop - 1) * gap;
    }
  }

  private exec_step(step: IKeyStep): void {
    if (step.kind === StepKind.Spawn) {
      if (step.spec) this.spawn_entity(step.spec, step.entity ?? this._entities.length);
      return;
    }
    const entity = step.entity === undefined ? this._actor : this._entities[step.entity];
    const ctrl = entity?.ctrl;
    if (!ctrl) return;
    const keys = this.parse_keys(step.keys, entity);
    if (!keys.length) return;
    if (step.kind === StepKind.Click) ctrl.click(...keys);
    else if (step.kind === StepKind.Down) ctrl.key_down(...keys);
    else ctrl.key_up(...keys);
  }

  private parse_keys(str: string, entity: Entity | null | undefined): LGK[] {
    const facing = (entity?.facing ?? FacingFlag.R) > 0 ? 1 : -1;
    const ret: LGK[] = [];
    for (const c of str) {
      switch (c) {
        case "L": ret.push(GK.L); break;
        case "R": ret.push(GK.R); break;
        case "U": ret.push(GK.U); break;
        case "D": ret.push(GK.D); break;
        case "a": ret.push(GK.a); break;
        case "j": ret.push(GK.j); break;
        case "d": ret.push(GK.d); break;
        case "F": ret.push(facing > 0 ? GK.R : GK.L); break;
        case "B": ret.push(facing > 0 ? GK.L : GK.R); break;
      }
    }
    return ret;
  }

  private spawn_actor(list: IMoveListData, move?: IMoveInfo): Entity | null {
    const data = this.lfw.datas.find(list.oid);
    if (!data) {
      this.warn(`[MoveTableLogic] fighter data not found: ${list.oid}`);
      return null;
    }
    const entity = this.lfw.factory.create_entity(this.world, data);
    if (!entity) return null;
    const { bg } = this.world;
    const x = list.x ?? ACTOR_X + (move?.dx ?? 0);
    const z = list.z ?? (bg.near + bg.far) / 2;
    entity.set_position(x, 0, z);
    entity.facing = list.facing ?? FacingFlag.R;
    entity.key_role = false;
    entity.name_visible = false;
    entity.attach();
    entity.team = list.team ?? TeamEnum.Team_1;
    entity.mp_max = DEMO_MP;
    entity.mp = DEMO_MP;
    entity.ctrl_visible = true;
    if (move?.stat_bar) {
      entity.key_role = true;
      entity.stat_bar_type = StatBarType.Float;
    }
    if (move?.hp !== undefined) entity.hp = move.hp;
    return entity;
  }

  private spawn_entities(spec: IMoveDemoEntity[]): void {
    this._entity_specs.push(...spec);
    this._entity_fit = this.calc_entity_fit(spec);
    for (let i = 0; i < spec.length; i++)
      if (spec[i].time === undefined) this.spawn_entity(spec[i], i);
  }

  private clamp_x(x: number): number {
    return Math.min(Math.max(x, DEMO_RECT_LEFT + DEMO_PAD), DEMO_RECT_RIGHT - DEMO_PAD);
  }

  private calc_entity_fit(spec: IMoveDemoEntity[]): number {
    const actor = this._actor;
    if (!actor || !spec.length) return 1;
    const facing = actor.facing > 0 ? 1 : -1;
    const ax = actor.position.x;
    const left_room = Math.max(ax - (DEMO_RECT_LEFT + DEMO_PAD), 0);
    const right_room = Math.max(DEMO_RECT_RIGHT - DEMO_PAD - ax, 0);
    let far_l = 0;
    let far_r = 0;
    for (const s of spec) {
      const dx = (s.x ?? ax + (s.dx ?? DEFAULT_ENTITY_DX) * facing) - ax;
      if (dx > far_r) far_r = dx;
      if (-dx > far_l) far_l = -dx;
    }
    const fit_l = far_l > left_room ? left_room / far_l : 1;
    const fit_r = far_r > right_room ? right_room / far_r : 1;
    return Math.min(fit_l, fit_r);
  }

  private spawn_entity(s: IMoveDemoEntity, index: number): void {
    const actor = this._actor;
    if (!actor) return;
    const facing = actor.facing > 0 ? 1 : -1;
    const { x: ax, z: az } = actor.position;
    const data = this.lfw.datas.find_object(s.oid);
    if (!data) {
      this.warn(`[MoveTableLogic] demo entity data not found: ${s.oid}`);
      return;
    }
    const entity = this.lfw.factory.create_entity(this.world, data);
    if (!entity) return;
    const raw_x = s.x ?? ax + (s.dx ?? DEFAULT_ENTITY_DX) * facing;
    const x = this.clamp_x(ax + (raw_x - ax) * this._entity_fit);
    const z = s.z ?? az + (s.dz ?? 0);
    entity.set_position(x, s.y ?? 0, z);
    entity.facing = s.facing ?? (x >= ax ? FacingFlag.L : FacingFlag.R);
    entity.key_role = false;
    entity.name_visible = false;
    entity.attach();
    entity.team = s.team ?? TeamEnum.Team_2;
    if (s.hp === undefined) entity.hp_max = entity.hp = DEFAULT_ENTITY_HP;
    else entity.hp = s.hp;
    if (s.stat_bar) {
      entity.key_role = true;
      entity.stat_bar_type = StatBarType.Float;
    }
    if (s.frame) {
      entity.mp = DEMO_MP;
      entity.enter_frame_by_id(s.frame, true);
    }
    if (s.tired) {
      const tired = Object.values(data.frames).find((f) => f.state === StateEnum.Tired);
      if (tired) entity.enter_frame_by_id(tired.id, true);
    }
    this._entities[index] = entity;
  }
}
